import { Alert } from '@/components/ui/alert';
import type { Payout } from '@/types/payout';
import { Button, Container, HStack, VStack } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AffiliatesList } from './AffiliatesList';
import { PayoutSummary } from './PayoutSummary';
import { SuccessHeader } from './SuccessHeader';

interface SuccessStateProps {
  payout: Payout | null;
  payoutScheduleOption: 'asap' | 'scheduled';
  formatScheduledDate: () => string;
  onBackToPayouts: () => void;
}

export const SuccessState: React.FC<SuccessStateProps> = ({
  payout,
  payoutScheduleOption,
  formatScheduledDate,
  onBackToPayouts,
}) => {
  const { t } = useTranslation();

  return (
    <Container maxW="3xl" pb={10}>
      <VStack gap={6} align="stretch">
        {/* Success Header */}
        <SuccessHeader
          payoutScheduleOption={payoutScheduleOption}
          formatScheduledDate={formatScheduledDate}
        />

        {/* Payout Summary */}
        <PayoutSummary payout={payout} formatScheduledDate={formatScheduledDate} />

        {/* Affiliates List */}
        <AffiliatesList payout={payout} />

        {/* Info Alert */}
        <Alert
          status="info"
          title={t('payouts.confirmation.success.whatNext')}
          variant="surface"
          rounded="xl"
          colorPalette="primary"
          animation="fadeIn 0.5s ease-out 0.6s both"
          css={{
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {t('payouts.confirmation.success.processingInfo')}
        </Alert>

        {/* Action Buttons */}
        <HStack
          gap={3}
          pt={4}
          animation="fadeIn 0.5s ease-out 0.8s both"
          css={{
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Button variant="outline" onClick={onBackToPayouts} flex={1}>
            {t('payouts.confirmation.success.backToPayouts')}
          </Button>
        </HStack>
      </VStack>
    </Container>
  );
};
